import axios from "axios";
import { APIResponse } from "../utils/types";
import { trackEvent } from "./analytics";

export const refreshToken = async () => {
  const response = await axios({
    url: "/api/auth/refresh-token",
    method: "post",
  });

  return response.data;
}

export const validateToken = async () => {
  try {
    const response = await axios({
      url: "/api/auth/validate-token",
      method: "post",
    });

    return response.data;
  }
  catch (error: any) {
    return {
      valid: false
    };
  }
}

export const signIn = async ({ email, password }: { email: string, password: string }): Promise<APIResponse> => {
  trackEvent("sign-in_request");

  try {
    const response = await axios({
      url: "/api/auth/sign-in",
      method: "post",
      data: {
        email,
        password
      }
    })

    trackEvent("sign-in_success");

    return {
      success: true,
      status: {
        type: "success",
      },
      data: response.data,
    };
  }
  catch (error: any) {
    if (error.response?.status === 401) {
      trackEvent("sign-in_invalid");

      return {
        success: false,
        status: {
          type: "warning",
          code: 401,
          message: "The email or password are invalid. Try again."
        },
      };
    }
    else {
      trackEvent("sign-in_error", { error: error.response?.message });

      return {
        success: false,
        status: {
          type: "error",
          message: "Something went wrong. Please try again later",
          detail: error.response?.message ? error.response.message : undefined,
          code: error.response?.status ? error.response.status : undefined,
        },
      };
    }
  }
}

export const signOut = async (): Promise<APIResponse> => {
  trackEvent("sign-out_request");
  try {
    const response = await axios({
      url: "/api/auth/sign-out",
      method: "post",
    })
    trackEvent("sign-out_success");
    return {
      success: true,
      status: {
        type: "success",
      },
    };
  }
  catch (error: any) {
    trackEvent("sign-out_error", { error: error.response?.message });
    return {
      success: false,
      status: {
        type: "error",
        message: "Something went wrong. Please try again later.",
        detail: error.response?.message ? error.response.message : undefined,
        code: error.response?.status ? error.response.status : undefined,
      },
    };
  }
}

export const signUp = async ({ email, password }: { email: string, password: string }): Promise<APIResponse> => {
  trackEvent("sign-up_request");

  try {
    const response = await axios({
      url: "/api/auth/sign-up",
      method: "post",
      data: {
        email,
        password
      }
    })

    trackEvent("sign-up_success");

    return {
      success: true,
      status: {
        type: "success",
      },
    };
  }
  catch (error: any) {
    if (error.response?.status === 409) {
      trackEvent("sign-up_invalid");

      return {
        success: false,
        status: {
          type: "warning",
          message: "An account with this email already exists",
          code: 409,
        },
      };
    }
    else {
      trackEvent("sign-up_error", { error: error.response?.message });

      return {
        success: false,
        status: {
          type: "error",
          message: "Something went wrong. Please try again later.",
          detail: error.response?.message ? error.response.message : undefined,
          code: error.response?.status ? error.response.status : undefined,
        },
      };
    }
  }
}

export const passwordResetRequest = async ({ email }: { email: string }): Promise<APIResponse> => {
  trackEvent("password-reset-request_request");

  try {
    const response = await axios({
      url: "/api/auth/password-reset/request",
      method: "post",
      data: {
        email
      }
    })

    trackEvent("password-reset-request_success");

    return {
      success: true,
      status: {
        type: "success",
      },
    };
  }
  catch (error: any) {
    trackEvent("password-reset-request_error", { error: error.response?.message });

    return {
      success: false,
      status: {
        type: "error",
        message: "Something went wrong. Please try again later.",
        detail: error.response?.message ? error.response.message : undefined,
        code: error.response?.status ? error.response.status : undefined,
      },
    };

  }
}

export const passwordResetConfirm = async ({ token, password }: { token: string, password: string }): Promise<APIResponse> => {
  trackEvent("password-reset-confirm_request");

  try {
    const response = await axios({
      url: "/api/auth/password-reset/confirm",
      method: "post",
      data: {
        token,
        password
      }
    })

    trackEvent("password-reset-confirm_success");

    return {
      success: true,
      status: {
        type: "success",
      },
    };
  }
  catch (error: any) {
    if (error.response?.status === 400) {
      trackEvent("password-reset-confirm_invalid");

      return {
        success: false,
        status: {
          type: "warning",
          code: 400,
          message: "The password is invalid. Try again."
        },
      };
    }
    else if (error.response?.status === 401) {
      trackEvent("password-reset-confirm_invalid");

      return {
        success: false,
        status: {
          type: "warning",
          code: 401,
          message: "The password is invalid. Try again."
        },
      };
    }
    else {
      trackEvent("password-reset-confirm_error", { error: error.response?.message });

      return {
        success: false,
        status: {
          type: "error",
          message: "Something went wrong. Please try again later.",
          detail: error.response?.message ? error.response.message : undefined,
          code: error.response?.status ? error.response.status : undefined,
        },
      };
    }
  }
}